import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { DashboardSummariesResponse } from '@shared/api/rest/dashboards';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface IPaymentProps {
  className?: string;
  mainAccount: DashboardSummariesResponse;
  subAccounts: DashboardSummariesResponse[];
}

export const PaymentItems = (props: IPaymentProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const { t } = useTranslation('dashboard');

  return (
    <div
      className={cn('flex min-h-32 bg-white dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'border-b border-gray-black pb-6 last:border-none')}
    >
      {PaletteUtils.isInsigniaPalette && <div className='w-16 min-w-16 bg-gradient-to-b from-black to-yellow-ocher' />}
      <div className={cn('w-full', PaletteUtils.isInsigniaPalette && 'px-3 py-3.5')}>
        <div className='gap-3'>
          {PaletteUtils.isVcfcPalette && (
            <Text className='mb-1 text-[10px] text-dark-wet-asphalt/60 dark:text-white/50'>{t('payment.account.titleVcfc')}</Text>
          )}
          {PaletteUtils.isVcfcPalette && <Text size='xl'>{props.mainAccount.iban}</Text>}
          {PaletteUtils.isInsigniaPalette && (
            <div className='flex justify-between'>
              <div className='flex gap-3'>
                <div className='mt-3 border-r border-light pr-3'>
                  <Text weight='semibold'>{props.mainAccount.accountName || FormatUtils.formatAccountNumber(props.mainAccount.accountNumber)}</Text>
                  <Text weight='semibold' size='xs' color={ColorsEnum.Gray} uppercase>
                    {props.mainAccount.productName}
                  </Text>
                </div>
                <div className='mt-3'>
                  <h3 className='text-base font-semibold text-soft-black dark:text-white'>{props.mainAccount.iban}</h3>
                  <h4 className='text-[10px] font-semibold uppercase text-pearl-dark-gray'>{t('payment.account.fields.iban')}</h4>
                </div>
              </div>
              <div className='flex h-full gap-1'>
                <div className='flex h-8 items-center rounded border px-2.5 text-base font-semibold text-light-gray'>
                  {CurrencyUtils.getSymbolByCode(props.mainAccount.currency)}
                </div>
                {props.subAccounts?.length > 0 &&
                  props.subAccounts.map((account) => {
                    return (
                      <div key={account.id} className='flex h-8 items-center rounded border px-2.5 text-base font-semibold text-light-gray'>
                        {CurrencyUtils.getSymbolByCode(account.currency)}
                      </div>
                    );
                  })}
                <div className='mx-2 flex cursor-pointer items-center' onClick={() => setIsCollapsed((prev) => !prev)}>
                  <div className={cn('-rotate-90 transition-transform duration-200', isCollapsed && 'rotate-90')}>
                    <Icon icon={<ChevronIcon />} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {PaletteUtils.isInsigniaPalette && <hr className='mx-0 my-3 text-light' />}
        {!isCollapsed && (
          <div className={cn('flex flex-col', PaletteUtils.isVcfcPalette && 'mt-4 gap-4', PaletteUtils.isInsigniaPalette && 'gap-3')}>
            <div
              key={props.mainAccount.id}
              className={cn(
                'flex gap-6',
                PaletteUtils.isInsigniaPalette && ['bg-ice px-3 py-2 dark:bg-slate', 'max-[1024px]:flex-col max-[1024px]:gap-2'],
                PaletteUtils.isVcfcPalette && [
                  'h-[52px] items-center justify-between rounded-lg bg-ghost-white px-4 dark:bg-light/5',
                  'max-md:h-full max-md:flex-col max-md:items-start max-md:gap-3 max-md:py-2',
                ],
              )}
            >
              <div
                className={cn(
                  'flex h-full items-center justify-center rounded border p-0.5 px-2 text-center font-semibold text-primary',
                  PaletteUtils.isVcfcPalette && ['border-none dark:text-white', 'max-md:p-0'],
                )}
              >
                {PaletteUtils.isInsigniaPalette && (
                  <Text className='ml-1 mr-2' weight='semibold' color={ColorsEnum.PrimaryOnly}>
                    {CurrencyUtils.getSymbolByCode(props.mainAccount.currency)}
                  </Text>
                )}
                <Text
                  className={cn(PaletteUtils.isInsigniaPalette && 'mt-0.5')}
                  weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
                  size='xs'
                  color={PaletteUtils.isVcfcPalette ? ColorsEnum.Primary : ColorsEnum.PrimaryOnly}
                  uppercase={PaletteUtils.isInsigniaPalette}
                >
                  {props.mainAccount.mainAccId ? t('payment.account.accountType.sub') : t('payment.account.accountType.main')}
                </Text>
                {PaletteUtils.isVcfcPalette && (
                  <div className='flex items-center'>
                    <div className='mx-1 h-1 w-1 rounded-full bg-ash-blue' />
                    <Text size='xs' weight='medium' uppercase>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.availableBalance, props.mainAccount.currency)}
                    </Text>
                  </div>
                )}
              </div>
              {PaletteUtils.isInsigniaPalette && (
                <>
                  <div>
                    <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.accountBalance')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.availableBalance, props.mainAccount.currency)}
                    </Text>
                  </div>
                  {/* <div>
                    <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.outgoingAmount')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.outgoingAmount, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                  {/* <div>
                    <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.incomingAmount')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.incomingAmount, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                  {/* <div>
                    <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.pendingTransactions')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.pendingTransactionsSummary, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                </>
              )}
              {PaletteUtils.isVcfcPalette && (
                <div className='flex gap-4 max-md:flex-col'>
                  {/* <div>
                    <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.outgoingAmount')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.outgoingAmount, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                  {/* <div>
                    <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.incomingAmount')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.incomingAmount, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                  {/* <div>
                    <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                      {t('payment.account.fields.pendingTransactions')}
                    </Text>
                    <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                      {AmountUtils.transformToAmountWithCurrency(props.mainAccount.pendingTransactionsSummary, props.mainAccount.currency)}
                    </Text>
                  </div> */}
                </div>
              )}
            </div>
            {props.subAccounts.map((account) => {
              return (
                <div
                  key={account.id}
                  className={cn(
                    'flex gap-6',
                    PaletteUtils.isInsigniaPalette && ['bg-ice px-3 py-2 dark:bg-slate', 'max-[1024px]:flex-col max-[1024px]:gap-2'],
                    PaletteUtils.isVcfcPalette && [
                      'h-[52px] items-center justify-between rounded-lg bg-ghost-white px-4 dark:bg-light/5',
                      'max-md:h-full max-md:flex-col max-md:items-start max-md:gap-3 max-md:py-2',
                    ],
                  )}
                >
                  <div
                    className={cn(
                      'flex h-full items-center justify-center rounded border p-0.5 px-2 text-center font-semibold text-primary',
                      PaletteUtils.isVcfcPalette && ['border-none dark:text-white', 'max-md:p-0'],
                    )}
                  >
                    {PaletteUtils.isInsigniaPalette && (
                      <Text className='ml-1 mr-2' weight='semibold' color={ColorsEnum.PrimaryOnly}>
                        {CurrencyUtils.getSymbolByCode(account.currency)}
                      </Text>
                    )}
                    <Text
                      className={cn(PaletteUtils.isInsigniaPalette && 'mt-0.5')}
                      weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined}
                      size='xs'
                      color={PaletteUtils.isVcfcPalette ? ColorsEnum.Primary : ColorsEnum.PrimaryOnly}
                      uppercase={PaletteUtils.isInsigniaPalette}
                    >
                      {t('payment.account.accountType.sub')}
                    </Text>
                    {PaletteUtils.isVcfcPalette && (
                      <div className='flex items-center'>
                        <div className='mx-1 h-1 w-1 rounded-full bg-ash-blue' />
                        <Text weight='medium' size='xs' uppercase>
                          {AmountUtils.transformToAmountWithCurrency(account.availableBalance, account.currency)}
                        </Text>
                      </div>
                    )}
                  </div>
                  {PaletteUtils.isInsigniaPalette && (
                    <>
                      <div>
                        <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.accountBalance')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.availableBalance, account.currency)}
                        </Text>
                      </div>
                      {/* <div>
                        <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.outgoingAmount')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.outgoingAmount, account.currency)}
                        </Text>
                      </div> */}
                      {/* <div>
                        <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.incomingAmount')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.incomingAmount, account.currency)}
                        </Text>
                      </div> */}
                      {/* <div>
                        <Text tag='h3' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.pendingTransactions')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.pendingTransactionsSummary, account.currency)}
                        </Text>
                      </div> */}
                    </>
                  )}
                  {PaletteUtils.isVcfcPalette && (
                    <div className='flex gap-4 max-md:flex-col'>
                      {/* <div>
                        <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.outgoingAmount')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.outgoingAmount, account.currency)}
                        </Text>
                      </div> */}
                      {/* <div>
                        <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.incomingAmount')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.incomingAmount, account.currency)}
                        </Text>
                      </div> */}
                      {/* <div>
                        <Text tag='h3' weight='light' size='xs' color={ColorsEnum.Secondary}>
                          {t('payment.account.fields.pendingTransactions')}
                        </Text>
                        <Text className='text-left' tag='h4' weight='semibold' size='xs'>
                          {AmountUtils.transformToAmountWithCurrency(account.pendingTransactionsSummary, account.currency)}
                        </Text>
                      </div> */}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
